import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../api/client";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import type { components } from "../api/types";

type AdminUser = components["schemas"]["User"];

// Admin Users — paginated table, click row → /users/:uid.
export function Users() {
  const { data: me } = useUser();

  const q = useInfiniteQuery({
    queryKey: ["admin", "users"],
    initialPageParam: "",
    queryFn: async ({ pageParam }) => {
      const { data, error } = await apiClient.GET("/api/v1/admin/users", {
        params: { query: pageParam ? { cursor: String(pageParam) } : {} },
      });
      if (error) throw error;
      return data as { users: AdminUser[]; next_cursor?: string };
    },
    getNextPageParam: (last) => last.next_cursor || undefined,
  });

  if (!me) return null;
  const users = q.data?.pages.flatMap((p) => p.users ?? []) ?? [];

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-6">
          Users
        </h1>
        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
        {q.error && (
          <p className="text-sm text-rose-300">
            Failed to load users: {(q.error as Error).message}
          </p>
        )}
        <Table>
          <THead>
            <TH>id</TH>
            <TH>email</TH>
            <TH>plan</TH>
            <TH>credits</TH>
            <TH>admin</TH>
            <TH>banned</TH>
            <TH>created</TH>
          </THead>
          <TBody>
            {users.map((u) => (
              <TR key={u.id}>
                <TD className="font-mono text-[11px] text-ink-300">
                  <Link to={`/users/${u.id}`} className="hover:text-accent">
                    {u.id.slice(0, 12)}…
                  </Link>
                </TD>
                <TD>{u.email}</TD>
                <TD>{u.plan}</TD>
                <TD className="tabular-nums">{u.credit_balance}</TD>
                <TD>{u.is_admin ? "✓" : ""}</TD>
                <TD>{u.banned_at ? "⊘" : ""}</TD>
                <TD className="font-mono text-[11px] text-ink-400">
                  {fmtDate(u.created_at)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {q.hasNextPage && (
          <button
            type="button"
            onClick={() => q.fetchNextPage()}
            className="mt-6 text-xs text-ink-400 hover:text-ink-100 transition-colors"
          >
            Load more →
          </button>
        )}
      </main>
    </div>
  );
}

function fmtDate(iso: string | undefined) {
  if (!iso) return "—";
  return new Date(iso).toISOString().slice(0, 10);
}
